import { useEffect, useState } from "react";
import { useReducedMotion } from "./motion";

/**
 * Types the given lines out one character at a time, the way a terminal
 * prints a prompt. Returns how many characters of the joined text are shown.
 * Reduced motion shows everything at once.
 */
export function useTyped(lines: string[], msPerChar = 28, startDelayMs = 150): { shown: string[]; done: boolean } {
  const reduced = useReducedMotion();
  const total = lines.reduce((n, l) => n + l.length, 0);
  const [count, setCount] = useState(reduced ? total : 0);

  useEffect(() => {
    if (reduced) {
      setCount(total);
      return;
    }
    let n = 0;
    let timer: ReturnType<typeof setTimeout>;
    const step = () => {
      n += 1;
      setCount(n);
      if (n < total) timer = setTimeout(step, msPerChar);
    };
    timer = setTimeout(step, startDelayMs);
    return () => clearTimeout(timer);
  }, [reduced, total, msPerChar, startDelayMs]);

  let left = count;
  const shown = lines.map((l) => {
    const take = Math.max(0, Math.min(l.length, left));
    left -= take;
    return l.slice(0, take);
  });
  return { shown, done: count >= total };
}
